import { usePatientRequestContext } from '@/feature/patients/context/usePatientRequestContext';
import {
  DEFAULT_REQUEST_ACTIONS,
  RetrievalMethod,
} from '@/feature/profiles/constants';
import { Circle, XCircle, CheckCircle2 } from 'lucide-react';
import { useFormContext } from '@predoc/cow';
import { validateMethodData } from './request-action-forms/validation';
import { cn } from '@/lib/utils';
import { format, parseISO } from 'date-fns';
import { REQUEST_ACTION_ORDER_MAP } from './request-action-forms/constants';

//TODO: update sidebar logic to show only sections that were selected in the pre-request step

const ActionStatusIcon = ({
  hasValidationError,
  hasValidationPassed,
}: {
  hasValidationError: boolean;
  hasValidationPassed: boolean;
}) => {
  if (hasValidationError) {
    return <XCircle className="text-destructive size-5" />;
  }

  if (hasValidationPassed) {
    return <CheckCircle2 className="size-5 fill-gray-900 text-white" />;
  }

  return <Circle className="size-5" />;
};

// Helper component to render values for each action type
const ActionValuesDisplay = ({
  method,
  formData,
}: {
  method: RetrievalMethod;
  formData: Record<string, unknown>; // Can be either RequestActionFormData or RequestActionFormsData
}) => {
  switch (method) {
    case RetrievalMethod.HIE: {
      const hieData = formData[RetrievalMethod.HIE] as
        | { alternativePostalCodes?: string[] }
        | undefined;
      const zipCodes = hieData?.alternativePostalCodes;
      if (zipCodes && zipCodes.length > 0) {
        return (
          <div className="text-muted-foreground text-xs">
            {zipCodes.join(', ')}
          </div>
        );
      }
      break;
    }

    case RetrievalMethod.FACESHEETS: {
      const facesheetsData = formData[RetrievalMethod.FACESHEETS] as
        | { selectedFacesheets?: { displayName: string }[] }
        | undefined;
      const selectedFacesheets = facesheetsData?.selectedFacesheets;
      if (selectedFacesheets && selectedFacesheets.length > 0) {
        return (
          <div className="text-muted-foreground text-xs">
            {selectedFacesheets
              .map(
                (facesheet: { displayName: string }) => facesheet.displayName,
              )
              .join(', ')}
          </div>
        );
      }
      break;
    }

    case RetrievalMethod.PROVIDER_FACILITY: {
      const providerData = formData[RetrievalMethod.PROVIDER_FACILITY] as
        | {
            retrieveFrom?: {
              providerName: string;
              facilityName: string;
              recordsRequested: string;
            }[];
          }
        | undefined;
      const retrieveFrom = providerData?.retrieveFrom;
      if (retrieveFrom && retrieveFrom.length > 0) {
        return (
          <div className="text-muted-foreground text-xs">
            {retrieveFrom
              .map(
                (provider: {
                  providerName: string;
                  facilityName: string;
                  recordsRequested: string;
                }) =>
                  `${provider.providerName} - ${provider.facilityName} (${provider.recordsRequested})`,
              )
              .join(', ')}
          </div>
        );
      }
      break;
    }

    case RetrievalMethod.RECORD_UPLOAD: {
      const uploadData = formData[RetrievalMethod.RECORD_UPLOAD] as
        | { uploadedFiles?: { name?: string; filename?: string }[] }
        | undefined;
      const uploadedFiles = uploadData?.uploadedFiles;
      if (uploadedFiles && uploadedFiles.length > 0) {
        return (
          <div className="text-muted-foreground text-xs">
            {uploadedFiles
              .map(
                (file: { name?: string; filename?: string }) =>
                  file.name || file.filename,
              )
              .join(', ')}
          </div>
        );
      }
      break;
    }

    case RetrievalMethod.MEDICATIONS:
      // no values to display
      break;
  }
  return null;
};

const isMethodComplete = (
  method: RetrievalMethod,
  formData: Record<string, unknown>,
) => {
  // HIE is considered complete once selected — zip codes are optional
  if (method === RetrievalMethod.HIE) {
    return formData[RetrievalMethod.HIE] !== undefined;
  }

  return validateMethodData(method, formData);
};

export const RequestSidebar = () => {
  const { requestState, currentStepIndex } = usePatientRequestContext();
  const patientData = requestState.patient;
  const selectedMethods = requestState.request?.selectedRequestMethods || [];
  const actionFormsData = requestState.request?.actionFormsData || {};

  const fullName =
    [patientData?.firstName, patientData?.lastName].filter(Boolean).join(' ') ||
    'Patient';
  const dateOfBirthLabel = patientData?.dateOfBirth
    ? `(${format(parseISO(patientData.dateOfBirth), 'MM/dd/yyyy')})`
    : '';

  // Only use real-time form data if we're in the request step
  const isRequestStep = currentStepIndex === 1;
  let currentFormData = actionFormsData;
  let formErrors: Record<string, unknown> = {};

  const formContext = useFormContext();
  // if request step, use form context otherwise use stored data
  if (formContext && isRequestStep) {
    currentFormData = formContext.getValues() || actionFormsData;
    formErrors = formContext.formState.errors || {};
  }

  return (
    <div className="border-border flex-1 border-l pl-4">
      <h1 className="mb-4 text-lg font-bold">
        {fullName} {dateOfBirthLabel}
      </h1>

      <div className="space-y-3">
        {DEFAULT_REQUEST_ACTIONS.filter((action) =>
          selectedMethods.includes(action.name as RetrievalMethod),
        )
          .sort(
            (a, b) =>
              REQUEST_ACTION_ORDER_MAP[a.name as RetrievalMethod] -
              REQUEST_ACTION_ORDER_MAP[b.name as RetrievalMethod],
          )
          .map((action) => {
            const method = action.name as RetrievalMethod;

            // Check for method-specific validation errors
            const hasError = !!formErrors[method];

            // Check if data is valid based on actual data content
            const hasValidData = isMethodComplete(method, currentFormData);

            const isSelected = selectedMethods.includes(method);
            const hasValidationPassed = hasValidData && !hasError;

            return (
              <div key={action.name} className="flex items-start gap-3">
                <ActionStatusIcon
                  hasValidationError={Boolean(hasError)}
                  hasValidationPassed={Boolean(hasValidationPassed)}
                />
                <div className="flex-1">
                  <span
                    className={cn(
                      'text-sm',
                      hasError ? 'text-destructive' : 'text-popover-foreground',
                    )}
                  >
                    {action.displayName}
                  </span>
                  {isSelected && (
                    <ActionValuesDisplay
                      method={method}
                      formData={currentFormData}
                    />
                  )}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};
